<template>
  <div class="page-content">
    <div class="page-header">
      <h1>Support</h1>
    </div>

    <Card>
      <template #header>
        <h3>Créer un ticket de support</h3>
      </template>
      <template #content>
        <form @submit.prevent="send" class="form-simple">
          <div v-if="error" class="error-message">
            <i class="pi pi-exclamation-triangle"></i>
            {{ error }}
          </div>
          <div v-if="success" class="success-message">
            <i class="pi pi-check-circle"></i>
            {{ success }}
          </div>
          <div class="form-field">
            <label>Email <span class="required">*</span></label>
            <InputText 
              v-model="contact.email" 
              :disabled="loading" 
              placeholder="votre@email.com"
              type="email"
              autocomplete="email"
              data-lpignore="false"
            />
          </div>
          <div class="form-field">
            <label>Titre <span class="required">*</span></label>
            <InputText 
              v-model="contact.title" 
              :disabled="loading" 
              placeholder="Résumé du problème"
              autocomplete="off"
              data-lpignore="true"
            />
          </div>
          <div class="form-field">
            <label>Description <span class="required">*</span></label>
            <Textarea 
              v-model="contact.message" 
              rows="6" 
              :disabled="loading" 
              placeholder="Décrivez votre problème en détail..."
              autocomplete="off"
              data-lpignore="true"
            />
          </div>
          <div class="form-field">
            <label>Priorité</label>
            <select v-model="contact.priority" :disabled="loading" class="priority-select">
              <option value="low">Basse</option>
              <option value="medium">Moyenne</option>
              <option value="high">Haute</option>
              <option value="urgent">Urgente</option>
            </select>
          </div>
          <Button 
            label="Créer le ticket" 
            class="p-button-success" 
            @click="send" 
            :loading="loading"
            :disabled="loading || !isFormValid"
            icon="pi pi-ticket"
            type="submit"
          />
        </form>
      </template>
    </Card>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import { useErrorHandler } from '@/composables/useErrorHandler';
import { useUser } from '@/composables/useUser';
import { ticketService } from '@/services/ticketService';

export default {
  name: 'Support',
  components: { Card, InputText, Textarea, Button },
  setup() {
    const route = useRoute();
    const toast = useToast();
    const { getErrorContext, clearErrorContext } = useErrorHandler();
    const { user, fetchUser } = useUser();
    const loading = ref(false);
    const error = ref(null);
    const success = ref(null);
    
    const contact = ref({ 
      email: '', 
      title: '',
      message: '', 
      priority: 'medium' 
    });

    // Check if form is valid
    const isFormValid = computed(() => {
      return contact.value.email.trim() !== '' && 
             contact.value.title.trim() !== '' && 
             contact.value.message.trim() !== '';
    });

    // Pre-fill form with error context if coming from an error
    onMounted(async () => {
      // Fetch current user to pre-fill email
      await fetchUser();
      if (user.value?.email) {
        contact.value.email = user.value.email;
      }

      if (route.query.error === 'true') {
        const errorContext = getErrorContext();
        if (errorContext) {
          // Pre-fill title with error message
          contact.value.title = errorContext.message 
            ? `Erreur: ${errorContext.message.substring(0, 80)}`
            : 'Erreur système';
          
          // Pre-fill message with error details
          const errorDetails = `Erreur rencontrée:
- Message: ${errorContext.message || 'N/A'}
- Statut: ${errorContext.status || 'N/A'} ${errorContext.statusText || ''}
- URL: ${errorContext.url || 'N/A'}
- Méthode: ${errorContext.method || 'N/A'}
- Date: ${errorContext.timestamp ? new Date(errorContext.timestamp).toLocaleString('fr-FR') : 'N/A'}
- Page: ${errorContext.currentPath || 'N/A'}
${errorContext.userAgent ? `- Navigateur: ${errorContext.userAgent}` : ''}`;
          
          contact.value.message = errorDetails;
          
          // Set priority based on error status
          if (errorContext.status >= 500) {
            contact.value.priority = errorContext.status === 503 || errorContext.status === 504 ? 'urgent' : 'high';
          }
          
          // Show info toast
          toast.add({ 
            severity: 'info', 
            summary: 'Contexte d\'erreur chargé', 
            detail: 'Les détails de l\'erreur ont été pré-remplis dans le formulaire.',
            life: 5000 
          });
        }
      }
    });

    const send = async () => {
      console.log('Send button clicked');
      console.log('Form data:', contact.value);
      console.log('Is form valid:', isFormValid.value);
      
      // Validate form
      if (!isFormValid.value) {
        error.value = 'Veuillez remplir tous les champs obligatoires';
        toast.add({ 
          severity: 'error', 
          summary: 'Erreur de validation', 
          detail: 'Veuillez remplir tous les champs obligatoires',
          life: 3000 
        });
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contact.value.email)) {
        error.value = 'Veuillez entrer une adresse email valide';
        toast.add({ 
          severity: 'error', 
          summary: 'Email invalide', 
          detail: 'Veuillez entrer une adresse email valide',
          life: 3000 
        });
        return;
      }

      loading.value = true;
      error.value = null;
      success.value = null;

      try {
        console.log('Starting ticket creation...');
        // Get error context if available
        const errorContext = getErrorContext();
        console.log('Error context:', errorContext);
        
        let ticketId;
        // Get service name from environment variable
        const serviceName = process.env.VUE_APP_TICKET_SERVICE_NAME || 'AgriEdge Master';
        console.log('Service name:', serviceName);
        
        if (errorContext) {
          console.log('Creating ticket from error context...');
          // Create ticket from error context
          ticketId = await ticketService.createTicketFromError(
            errorContext,
            contact.value.email,
            serviceName
          );
        } else {
          console.log('Creating regular ticket...');
          const ticketData = {
            title: contact.value.title.trim(),
            description: contact.value.message.trim(),
            service: serviceName,
            createdBy: contact.value.email.trim(),
            priority: contact.value.priority,
            status: process.env.VUE_APP_TICKET_DEFAULT_STATUS || 'open',
            platform: 'ASARI IRRIGATION PLATEFOLME', // Platform identifier
          };
          console.log('Ticket data:', ticketData);
          // Create regular ticket
          try {
            ticketId = await ticketService.createTicket(ticketData);
            console.log('✅ Ticket created with ID:', ticketId);
          } catch (ticketError) {
            console.error('❌ Failed to create ticket:', ticketError);
            throw ticketError;
          }
        }

        // Success
        success.value = `Ticket créé avec succès (ID: ${ticketId})`;
        toast.add({ 
          severity: 'success', 
          summary: 'Ticket créé', 
          detail: `Votre ticket a été créé avec succès. ID: ${ticketId}`,
          life: 5000 
        });

        // Clear form and error context
        clearErrorContext();
        contact.value = { 
          email: user.value?.email || '', 
          title: '',
          message: '', 
          priority: 'medium' 
        };

        // Clear success message after 5 seconds
        setTimeout(() => {
          success.value = null;
        }, 5000);
      } catch (err) {
        console.error('Error creating ticket:', err);
        console.error('Error stack:', err.stack);
        const errorMsg = err.message || 'Erreur lors de la création du ticket';
        error.value = errorMsg;
        toast.add({ 
          severity: 'error', 
          summary: 'Erreur', 
          detail: errorMsg,
          life: 5000 
        });
      } finally {
        loading.value = false;
        console.log('Loading set to false');
      }
    };

    return { contact, loading, error, success, isFormValid, send };
  }
};
</script>

<style scoped>
.page-content {
  padding: 1.5rem;
}

.page-header h1 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  color: #1a1a1a;
}

.card {
  margin-bottom: 1rem;
}

.card-header h3 {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
}

.form-simple {
  padding: 1rem 0;
}

.form-field {
  margin-bottom: 1rem;
}

.form-field label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
}

.form-field label .required {
  color: #ef4444;
}

.priority-select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.875rem;
  background-color: white;
}

.priority-select:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.error-message {
  padding: 0.75rem;
  margin-bottom: 1rem;
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 4px;
  color: #c33;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.success-message {
  padding: 0.75rem;
  margin-bottom: 1rem;
  background-color: #efe;
  border: 1px solid #cfc;
  border-radius: 4px;
  color: #3c3;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .page-content {
    padding: 1rem;
  }
}
</style>
